import { Stack } from "@chakra-ui/react";
import {
  AssetInput,
  PolicyAssetNode,
  useCreateAssetMutation,
  useCreatePolicyDocumentMutation,
  useCreatePolicyMutation,
  useCreatePolicyNoteMutation,
  useCreateRegistryMutation,
  useGetRegistriesBySearchLazyQuery,
  useGetAssetByIdForReviewLazyQuery,
  GetAssetByIdForReviewQuery,
  Exact,
} from "graphql/queries/generated/queries";
import { NewPolicyForm, NewAssetDrawer, ImportAssetsDrawer } from "pages/spin/shared";
import { useForm, FormProvider, useFieldArray, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { SelectedAsset } from "components/ui/PolicyAssetSearchBox/types";
import { PolicyInputExtended } from "pages/spin/types";
import { PolicyInputSchema } from "graphql/queries/overrides";
import { ValidationProvider } from "components/form/ValidationContext";
import defaultValues from "pages/spin/shared/defaultValues";
import { BaseRow } from "react-csv-importer";
import { useTenantContext } from "contexts/TenantContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useGetOrCreateRegistry from "hooks/useGetOrCreateRegistry";
import useGetAssetCurrentValue from "hooks/useGetAssetCurrentValue";
import parseBooleanString from "helpers/parseBooleanString";
import useStagger from "helpers/useStagger";
import { LazyQueryExecFunction } from "@apollo/client";
import Overlay from "components/ui/Overlay";
import { handleMutation, toast } from "middleware/Toaster";
import { SPIN_ROUTES } from "routes/constants";
import { parseObjectToCreateEntity } from "helpers/processCsvRow";
import useGeolocation from "hooks/useGeolocation";

const policyAssetFields = {
  asset: "",
  catNat: false,
  coverTerrorism: false,
  coveredRiskType: "",
  coverType: "",
  evaluationType: "",
  exemption: false,
  title: "",
  author: "",
  creationPeriod: "",
  objectLocationEntityId: "",
  objectLocationOfficeId: "",
  insuredValue: {
    amount: null as number | null,
    currency: "EUR",
  },
};

const IMPORT_FIELDS = [
  { name: "title", optional: false },
  { name: "author", optional: true },
  { name: "creationPeriod", optional: true },
  { name: "technique", optional: true },
  { name: "netHeight", optional: true },
  { name: "netWidth", optional: true },
  { name: "netDepth", optional: true },
  {
    name: "netLengthUnit",
    optional: true,
  },
  { name: "inventoryNumber", optional: true },
  { name: "assetPresentValueAmount", optional: true },
  { name: "assetPresentValueCurrency", optional: true },
  { name: "coveredRiskType", optional: true },
  { name: "coverType", optional: true },
  { name: "evaluationType", optional: true },
  { name: "exemption", optional: true },
  { name: "coverTerrorism", optional: true },
  { name: "catNat", optional: true },
  { name: "dimensionsNotes", optional: true },
];

const PCPoliciesNewPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [showNewAssetDrawer, setShowNewAssetDrawer] = useState<boolean>(false);
  const [showImportAssetsDrawer, setShowImportAssetsDrawer] = useState<boolean>(false);
  const [tabIndex, setTabIndex] = useState(0);
  const { tenant } = useTenantContext();
  const getAssetCurrentValue = useGetAssetCurrentValue();
  const [geolocateAddress] = useGeolocation();
  const methods = useForm<PolicyInputExtended>({
    defaultValues,
    resolver: yupResolver(PolicyInputSchema()),
  });

  const getOrCreateRegistry = useGetOrCreateRegistry();
  const [createAsset, { data: createAssetData, error: createAssetError }] = useCreateAssetMutation({
    ...handleMutation("Asset created!"),
  });
  const [
    createPolicy,
    { data: createPolicyData, error: createPolicyError, loading: createPolicyLoading },
  ] = useCreatePolicyMutation({
    ...handleMutation("Policy created!"),
  });

  const [BROKEN_getAssetByIdForReview] = useGetAssetByIdForReviewLazyQuery();
  const getAssetByIdForReview: LazyQueryExecFunction<
    GetAssetByIdForReviewQuery,
    Exact<{
      id: string;
    }>
  > = useStagger(BROKEN_getAssetByIdForReview);

  const [
    createPolicyDocument,
    { data: createPolicyDocumentData, error: createPolicyDocumentError },
  ] = useCreatePolicyDocumentMutation({
    ...handleMutation("Document created!"),
  });
  const [createPolicyNote, { data: createPolicyNoteData, error: createPolicyNoteError }] =
    useCreatePolicyNoteMutation({
      ...handleMutation("Note created!"),
    });

  const [searchRegistry] = useGetRegistriesBySearchLazyQuery();
  const [createRegistry] = useCreateRegistryMutation({
    ...handleMutation("Registry created!"),
  });

  const {
    formState: { errors },
    setFocus,
    setValue,
  } = methods;

  useEffect(() => {
    if (createPolicyData) {
      toast({
        title: t("Policy has been created."),
        description: t(""),
        status: "success",
        duration: 9000,
        isClosable: true,
      });

      const {
        createPolicy: { policy },
      } = createPolicyData;
      setTimeout(() => {
        navigate(`${SPIN_ROUTES.POLICY_PC}/${policy.id}`);
      }, Number(process.env.REACT_APP_REDIRECT_TIMEOUT) ?? 3000);

      return;
    }

    if (createPolicyError)
      toast({
        title: t("Policy couldn't be created."),
        description: t(`Error: ${createPolicyError.message}`),
        status: "error",
        duration: 9000,
        isClosable: true,
      });
  }, [createPolicyData, createPolicyError]);

  useEffect(() => {
    const firstError = Object.keys(errors).reduce(
      (field, a) => ((errors as unknown as never)[field] ? field : a),
      null
    );

    try {
      if (firstError) {
        setTabIndex(0);
        setFocus(firstError as keyof PolicyInputExtended);
      }
    } catch (e) {
      console.log(e);
    }
  }, [errors, setFocus]);

  const { fields, append, remove, update } = useFieldArray({
    control: methods.control,
    name: "policyAssets",
  });
  const {
    fields: noteFields,
    append: appendNote,
    remove: removeNote,
  } = useFieldArray({
    control: methods.control,
    name: "policyNote",
  });
  const {
    fields: documentFields,
    append: appendDocument,
    remove: removeDocument,
  } = useFieldArray({
    control: methods.control,
    name: "policyDocuments",
  });

  const watchPolicyAssets = methods.watch("policyAssets");

  useEffect(() => {
    console.log({ watchPolicyAssets });
    if (!watchPolicyAssets || watchPolicyAssets.length === 0) return;
    // eslint-disable-next-line array-callback-return
    const totalValue = watchPolicyAssets.reduce((accumulator, { insuredValue }) => {
      if (!Number.isNaN(insuredValue.amount)) {
        return accumulator + Number(insuredValue.amount);
      }
      return accumulator;
    }, 0);

    setValue("totalInsuredValue.amount", totalValue, {
      shouldTouch: true,
      shouldDirty: true,
    });
  }, [watchPolicyAssets]);

  const goToNextTab = () => {
    setTabIndex((prevIndex) => (prevIndex < 3 && prevIndex >= 0 ? prevIndex + 1 : prevIndex));
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };
  const goToPreviousTab = () => {
    setTabIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : prevIndex));
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  const handleTabChange = (index: number) => {
    setTabIndex(index);
  };

  const addPolicyAsset = async ({
    id,
    title,
    author,
    creationPeriod,
    objectID,
    gqlId,
    image,
  }: Partial<SelectedAsset>) => {
    const {
      evaluationType,
      catNat,
      coverTerrorism,
      coveredRiskType,
      coverType,
      eventEntityId,
      eventLocationEntityId,
      exemption,
    } = methods.getValues();

    const { amount, currency } = (await getAssetCurrentValue(gqlId)) || {};
    const {
      data: getAssetByIdForReviewData,
      loading: getAssetByIdForReviewLoading,
      error: getAssetByIdForReviewError,
    } = await getAssetByIdForReview({ variables: { id: gqlId } });
    // console.log("eventEntity: ", eventEntityId)
    append({
      ...policyAssetFields,
      asset: gqlId,
      title,
      author,
      creationPeriod,
      evaluationType,
      catNat,
      coverTerrorism,
      coveredRiskType,
      coverType,
      exemption,
      insuredValue: {
        amount: amount ?? null,
        currency: currency ?? "EUR",
      },
      ...getAssetByIdForReviewData.asset,
      objectLocationEntityId: eventEntityId,
      objectLocationOfficeId: eventLocationEntityId,
    });
  };

  const onSubmit: SubmitHandler<PolicyInputExtended> = async (formData, e) => {
    const { policyAssets, policyDocuments, policyNote, ...policyData } = formData;

    const cleanPolicyAssets = policyAssets.map((policyAsset) => {
      const {
        title,
        creationPeriod,
        category,
        creationDate,
        genericDimensions,
        inventoryNumber,
        technique,
        image,
        mainImage,
        id,
        author,
        authorEntity,
        actualDateAndPeriod,
        __typename,
        ...policyAssetData
      } = policyAsset;
      return policyAssetData;
    });

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const hasFile = policyDocuments.some(({ file }) => (file as unknown as FileList).length! > 0);

    const { data: createPolicyEvalutedData, errors: createPolicyEvaluatedErrors } =
      await createPolicy({
        variables: {
          input: {
            policyData: {
              ...policyData,
              policyAssets: cleanPolicyAssets,
              policyDocuments: hasFile ? policyDocuments : null,
              policyNote,
            },
          },
        },
      });

    if (createPolicyEvaluatedErrors) return;

    const {
      createPolicy: {
        policy: { id: policyId },
      },
    } = createPolicyEvalutedData;
  };

  const parseRow = async ({
    row,
    index,
  }: {
    row: AssetInput &
      PolicyAssetNode & { assetPresentValueAmount: string } & { author: string } & {
        assetPresentValueCurrency: string;
      };
    index: number;
  }) => {
    const { author, lender, shipper, ...copiedRow } = row;
    if (!row.category) {
      copiedRow.category = "not_validated";
    }

    if (!row.subcategory) {
      copiedRow.subcategory = "";
    }

    if (row.title.trim() === "") {
      console.log("TEST CSV", row, row.title, row.title.trim(), row.title.trim() === "");
      toast({
        title: t(`Asset at row ${index} couldn't be uploaded`),
        description: t(`Error: Asset is missing title, which is a required field.`),
        status: "error",
        duration: 9000,
        isClosable: true,
      });

      return null;
    }
    try {
      const authorEntityId = await getOrCreateRegistry(row.author as unknown as string);

      const parsedAssetPresentValueAmount = row?.assetPresentValueAmount?.replace(/\D/g, "");

      return {
        ...copiedRow,
        category: "not_validated",
        subcategory: "",
        authorEntityId,
        netWidth: parseFloat(row.netWidth as unknown as string),
        netHeight: parseFloat(row.netHeight as unknown as string),
        netDepth: parseFloat(row.netDepth as unknown as string),
        assetPresentValueAmount: parsedAssetPresentValueAmount,
      };
    } catch (error) {
      console.log(error);
      throw error;
    }
  };

  const processCsvRows = async (rows: BaseRow[], { startIndex }: { startIndex: number }) => {
    await Promise.all(
      // eslint-disable-next-line consistent-return
      rows.map(
        async (
          row: AssetInput &
            PolicyAssetNode & { author: string } & { assetPresentValueAmount: string } & {
              assetPresentValueCurrency: string;
            },
          index: number
        ) => {
          try {
            const entities = await parseObjectToCreateEntity(row, getOrCreateRegistry);
            console.log(entities);
          } catch (e) {
            console.error("TEST", e);
            throw e;
          }

          const parsedRow = await parseRow({ row, index });
          const {
            evaluationType: evaluationTypeRow,
            coverTerrorism: coverTerrorismRow,
            catNat: catNatRow,
            assetPresentValueAmount: assetPresentValueAmountRow,
            coveredRiskType: coveredRiskTypeRow,
            coverType: coverTypeRow,
            exemption: exemptionRow,
            lenderAddress: lenderAddressRow,
            authorEntityId,
            assetPresentValueCurrency,
            ...assetInput
          } = parsedRow;

          const { data } = await createAsset({
            variables: {
              input: {
                assetData: {
                  ...assetInput,
                  authorEntityId,
                  validated: false,
                  creationDateNotes: assetInput.creationPeriod,
                  carryingValueAmount: {
                    amount: Number(assetPresentValueAmountRow),
                    currency: assetPresentValueCurrency ?? "EUR",
                  },
                },
              },
            },
          });

          if (!data.createAsset) throw new Error("Error importing asset");
          const {
            createAsset: { asset },
          } = data;

          const { id, title, author, creationPeriod } = asset;

          const {
            evaluationType,
            catNat,
            coverTerrorism,
            coveredRiskType,
            coverType,
            eventEntityId,
            eventLocationEntityId,
            exemption,
          } = methods.getValues();

          try {
            append({
              ...policyAssetFields,
              asset: id,
              title,
              author,
              creationPeriod,
              evaluationType: row.evaluationType ?? evaluationType,
              catNat: parseBooleanString(catNatRow as unknown as string) ?? catNat,
              coverTerrorism:
                parseBooleanString(coverTerrorismRow as unknown as string) ?? coverTerrorism,
              exemption: parseBooleanString(exemptionRow as unknown as string) ?? exemption,
              coveredRiskType: row.coveredRiskType ?? coveredRiskType,
              coverType: row.coverType ?? coverType,
              objectLocationEntityId: eventEntityId,
              objectLocationOfficeId: eventLocationEntityId,
              insuredValue: {
                amount: Number(assetPresentValueAmountRow),
                currency: assetPresentValueCurrency ?? "EUR",
              },
              status: "NEW",
            });
          } catch (error) {
            console.log(error);
            toast({
              title: t("Something went wrong while importing assets"),
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              description: error instanceof Error ? error.message : "",
              status: "success",
              duration: 9000,
              isClosable: true,
            });
            throw error;
          }
        }
      )
    );
  };

  return (
    <>
      <Overlay show={createPolicyLoading} />
      <Stack spacing="4">
        <ValidationProvider schema={PolicyInputSchema()}>
          <FormProvider {...methods}>
            <NewPolicyForm
              getValues={methods.getValues}
              policyAssets={watchPolicyAssets}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={methods.handleSubmit(onSubmit)}
              goToNextTab={goToNextTab}
              goToPreviousTab={goToPreviousTab}
              handleTabChange={handleTabChange}
              tabIndex={tabIndex}
              data={createPolicyData}
              loading={methods.formState.isSubmitting}
              error={[createPolicyError, createPolicyNoteError, createPolicyDocumentError]}
              extraFields={{ fields, append, remove, update }}
              setShowNewAssetDrawer={setShowNewAssetDrawer}
              setShowImportAssetsDrawer={setShowImportAssetsDrawer}
              addPolicyAsset={addPolicyAsset}
              policyType="PERMANENT_COLLECTION"
            />
          </FormProvider>
        </ValidationProvider>
        <NewAssetDrawer
          show={showNewAssetDrawer}
          onClose={() => setShowNewAssetDrawer(false)}
          addPolicyAsset={addPolicyAsset}
        />
        <ImportAssetsDrawer
          show={showImportAssetsDrawer}
          onClose={() => setShowImportAssetsDrawer(false)}
          processChunk={processCsvRows}
          fields={IMPORT_FIELDS}
        />
      </Stack>
    </>
  );
};
export default PCPoliciesNewPage;
